// voiceActions.js — Single source of truth for every voice-driven action on the scanner.
// (c) 2026-08-30 — Hermes/Kara/Lala Agent Tab.
//
// WHY THIS EXISTS
// ───────────────
// Hermes (the LLM) speaks plain English to the user, then emits JSON action blocks
// inside its reply. The frontend extracts those blocks and runs them through
// `executeAction(act, ctx)` below. Every action the scanner exposes — switching
// tabs, swapping strategies, reading balance, running backtests, trading, charting —
// lives here, with: natural-language examples, danger level (needs-confirm), and
// the actual executor.
//
// USAGE FROM AgentTab.jsx:
//   import { VOICE_ACTIONS, executeAction } from "./voiceActions";
//   const result = await executeAction(act, ctx);  // ctx = { setTab, fetchBalance, ... }
//
// USAGE FROM AmberScanner.jsx (chat):
//   Already wired at line ~4790 in `for (const act of (resp.actions||[]))`.
//   Add new actions here so both UIs use the same registry.

import React from "react";
import { check as confirmGateCheck, describeAction } from "./voiceConfirmGate";

// ─────────────────────────────────────────────────────────────────────────────
// ACTION REGISTRY — the canonical list. Edit this and both UIs pick it up.
// Each entry:
//   id          — action.type emitted by Hermes
//   label       — human-readable name shown in the Voice Command Library panel
//   desc        — one-line summary
//   examples    — phrases the user can say
//   danger      — "read" (no confirm), "moderate" (confirm 1×), "destructive" (confirm 2×)
//   group       — sidebar grouping in the UI ("Navigation", "Trading", "Account", ...)
//   icon        — emoji
// ─────────────────────────────────────────────────────────────────────────────
export const VOICE_ACTIONS = [
  // ── Navigation ────────────────────────────────────────────────────────────
  { id: "set_tab",            label: "Switch tab",           desc: "Open any scanner tab by name", danger: "read",
    group: "Navigation", icon: "🧭",
    examples: ["switch to charts", "open history", "go to settings", "show strategy", "take me to the scanner"] },
  { id: "open_chat",          label: "Open chat panel",      desc: "Show the floating chat box", danger: "read",
    group: "Navigation", icon: "💬",
    examples: ["open chat", "show chat panel"] },
  { id: "close_chat",         label: "Close chat panel",     desc: "Hide the floating chat box", danger: "read",
    group: "Navigation", icon: "💬",
    examples: ["close chat", "hide chat panel"] },

  // ── Voice controls (Kara/Lala themselves) ────────────────────────────────
  { id: "toggle_voice",       label: "Mute / unmute voice",  desc: "TTS on or off", danger: "moderate",
    group: "Voice", icon: "🔊",
    examples: ["mute yourself", "be quiet", "unmute", "speak again"] },
  { id: "toggle_wake_word",   label: "Wake word on/off",     desc: "Always-on 'Hey Kara'/'Hey Lala'", danger: "moderate",
    group: "Voice", icon: "👂",
    examples: ["enable wake word", "disable wake word", "always listening on"] },
  { id: "set_personality",    label: "Personality mode",     desc: "Casual vs formal Kara/Lala", danger: "moderate",
    group: "Voice", icon: "🎭",
    examples: ["be formal", "switch to casual", "normal mode"] },

  // ── Symbol management ─────────────────────────────────────────────────────
  { id: "add_symbol",         label: "Add symbol",           desc: "Subscribe to scanner feed for a symbol", danger: "moderate",
    group: "Symbols", icon: "➕",
    examples: ["add XAUUSD", "watch EURUSD", "subscribe to R_25"] },
  { id: "remove_symbol",      label: "Remove symbol",        desc: "Unsubscribe a symbol from feed", danger: "moderate",
    group: "Symbols", icon: "➖",
    examples: ["stop watching EURUSD", "remove XAUUSD"] },
  { id: "focus_symbol",       label: "Focus symbol",         desc: "Highlight one symbol in scanner", danger: "read",
    group: "Symbols", icon: "🎯",
    examples: ["focus R_100", "show me R_25 details"] },
  { id: "add_all_symbols",    label: "Add all symbols",      desc: "Subscribe scanner to every pair", danger: "moderate",
    group: "Symbols", icon: "🌍",
    examples: ["watch everything", "add all symbols"] },
  { id: "clear_symbols",      label: "Clear all symbols",    desc: "Unsubscribe scanner from all pairs", danger: "destructive",
    group: "Symbols", icon: "🧹",
    examples: ["clear all symbols", "stop watching everything"] },

  // ── Strategy ──────────────────────────────────────────────────────────────
  { id: "activate_strategy",  label: "Activate strategy",    desc: "Swap the active strategy on your account", danger: "destructive",
    group: "Strategy", icon: "🎯",
    examples: ["swap to VIX Beast", "activate SMC EMA13", "use the Forex scalper"] },
  { id: "show_strategy_tab",  label: "Open Strategy manager",desc: "Navigate to the Strategy tab", danger: "read",
    group: "Strategy", icon: "📂",
    examples: ["show strategies", "open the strategy library"] },
  { id: "create_strategy",    label: "Create new strategy",  desc: "Compose & save a new strategy file", danger: "destructive",
    group: "Strategy", icon: "✨",
    examples: ["create a strategy called X based on Y"] },

  // ── Charts ────────────────────────────────────────────────────────────────
  { id: "set_chart_symbol",   label: "Chart symbols",        desc: "Set which symbols the chart tab shows", danger: "moderate",
    group: "Charts", icon: "📈",
    examples: ["chart XAUUSD", "show me EURUSD and GBPUSD on charts"] },
  { id: "set_chart_timeframe",label: "Chart timeframe",      desc: "Set chart interval (1m, 5m, 15m, 1h, 4h, 1d)", danger: "moderate",
    group: "Charts", icon: "⏱️",
    examples: ["5 minute charts", "switch charts to 1 hour"] },
  { id: "set_chart_layout",   label: "Chart layout",         desc: "single / dual / quad chart layout", danger: "moderate",
    group: "Charts", icon: "🪟",
    examples: ["dual chart", "quad view"] },
  { id: "open_charts",        label: "Open Charts tab",      desc: "Switch to the Charts tab", danger: "read",
    group: "Charts", icon: "📈",
    examples: ["open charts", "show charts"] },

  // ── Trading ───────────────────────────────────────────────────────────────
  { id: "execute_trade",      label: "Execute trade",        desc: "Place a live trade (CALL/PUT) on a symbol", danger: "destructive",
    group: "Trading", icon: "💸",
    examples: ["buy 5 dollars of R_100", "put $1 on EURUSD now"] },
  { id: "set_stake",          label: "Set stake amount",     desc: "Change the default $ per trade", danger: "moderate",
    group: "Trading", icon: "💰",
    examples: ["set stake to 10 dollars", "use 2 dollar stakes"] },
  { id: "toggle_auto_trade",  label: "Auto-trade on/off",    desc: "Auto-execute on every confirmed signal", danger: "destructive",
    group: "Trading", icon: "🤖",
    examples: ["enable auto trade", "disable auto trade", "turn off auto"] },

  // ── Alerts ────────────────────────────────────────────────────────────────
  { id: "toggle_alerts",      label: "Alerts on/off",        desc: "Enable or mute scanner alerts", danger: "moderate",
    group: "Alerts", icon: "🔔",
    examples: ["mute alerts", "alerts back on", "be quiet on signals"] },
  { id: "toggle_alert_voice", label: "Alert voice on/off",   desc: "Speak alerts aloud or just notify", danger: "moderate",
    group: "Alerts", icon: "🗣️",
    examples: ["voice alerts on", "silent alerts"] },
  { id: "clear_alerts",       label: "Clear alert log",      desc: "Empty the alert history", danger: "moderate",
    group: "Alerts", icon: "🧹",
    examples: ["clear alerts", "wipe the alert log"] },

  // ── History ───────────────────────────────────────────────────────────────
  { id: "clear_history",      label: "Clear trade history",  desc: "Wipe local trade history (irreversible)", danger: "destructive",
    group: "History", icon: "🗑️",
    examples: ["clear history", "wipe trades"] },

  // ── Telegram / Notifications ──────────────────────────────────────────────
  { id: "send_telegram",      label: "Send Telegram",        desc: "Push a message to your Telegram bot", danger: "moderate",
    group: "Notifications", icon: "✈️",
    examples: ["send me a telegram", "ping my phone"] },

  // ── Backtests ─────────────────────────────────────────────────────────────
  { id: "run_backtest",       label: "Run advanced backtest",desc: "Run a backtest via advanced_backtester", danger: "moderate",
    group: "Backtests", icon: "🧪",
    examples: ["backtest R_100", "run a backtest on my strategy"] },
  { id: "show_stratlab_tab",  label: "Open Strategy Lab",    desc: "Open the TradingView AI Optimizer tab", danger: "read",
    group: "Backtests", icon: "�",
    examples: ["open strat lab", "show strategy lab"] },

  // ── AI scans ──────────────────────────────────────────────────────────────
  { id: "ai_scan",            label: "AI scan",              desc: "Run multi-factor AI scan on a symbol", danger: "moderate",
    group: "AI", icon: "🧠",
    examples: ["AI scan R_100", "scan XAUUSD with AI"] },
  { id: "delegate_to_scout",  label: "Scout (web/news)",     desc: "Search the web or pull the economic calendar", danger: "read",
    group: "AI", icon: "🛰️",
    examples: ["search for NFP news", "what's the economic calendar today"] },
  { id: "delegate_to_strat",  label: "Strat (verdict)",      desc: "Delegate to Stratos for a verdict", danger: "read",
    group: "AI", icon: "⚖️",
    examples: ["strat verdict on R_25"] },
  { id: "delegate_to_cortex", label: "Cortex (deep analysis)", desc: "Deep AI market analysis on a symbol", danger: "read",
    group: "AI", icon: "🧬",
    examples: ["cortex analysis on EURUSD"] },
  { id: "delegate_to_toolsmith", label: "Toolsmith (any tool)", desc: "Call any MCP tool by name", danger: "moderate",
    group: "AI", icon: "🛠️",
    examples: ["use the price proposal tool", "toolsmith: brave search X"] },

  // ── Reads — NEW: AgentTab voice can query these via Hermes ────────────────
  { id: "read_balance",       label: "Read balance",         desc: "Speak current account balance", danger: "read",
    group: "Account", icon: "💵",
    examples: ["what's my balance?", "how much money do I have?", "what's my account worth"] },
  { id: "read_account_mode",  label: "Read account mode",    desc: "Demo or live account status", danger: "read",
    group: "Account", icon: "🏦",
    examples: ["am I on demo?", "am I on live?", "what account am I using"] },
  { id: "read_active_strategy",label: "Read active strategy", desc: "Speak which strategy is active", danger: "read",
    group: "Strategy", icon: "📌",
    examples: ["what strategy is active?", "what am I trading?", "which strategy am I on"] },
  { id: "read_open_trades",   label: "Read open trades",     desc: "Speak how many trades are open + details", danger: "read",
    group: "Trading", icon: "📂",
    examples: ["what trades are open?", "how many open trades?", "list my trades"] },
  { id: "read_history_summary",label:"Read history summary", desc: "Today's P&L, win rate, total trades", danger: "read",
    group: "History", icon: "📊",
    examples: ["how am I doing today?", "today's win rate", "what's my P&L?"] },
  { id: "read_ml_status",     label: "Read ML status",       desc: "ML engine health + last accuracy", danger: "read",
    group: "AI", icon: "🤖",
    examples: ["is ML online?", "ML accuracy", "how's the ML engine"] },
  { id: "read_list_strategies",label:"List strategies",      desc: "Speak all available strategies", danger: "read",
    group: "Strategy", icon: "📚",
    examples: ["what strategies do I have?", "list all strategies"] },
  { id: "read_market_conditions",label:"Read market conditions", desc: "Current regime, sessions, regime scores", danger: "read",
    group: "AI", icon: "🌐",
    examples: ["what's the market doing?", "current market conditions", "what session is it"] },
  { id: "read_best_hours",    label: "Read best hours",      desc: "Best UTC hours to trade", danger: "read",
    group: "Analytics", icon: "🕐",
    examples: ["when should I trade?", "best trading hours"] },
  { id: "read_signals",       label: "Read latest signals",  desc: "Speak the most recent scanner signals", danger: "read",
    group: "Alerts", icon: "📡",
    examples: ["any signals?", "latest signal", "what's the scanner seeing"] },

  // ── Speak-only ────────────────────────────────────────────────────────────
  { id: "speak",              label: "Speak (custom text)",  desc: "Make Kara/Lala say something specific", danger: "read",
    group: "Voice", icon: "📣",
    examples: ["say 'good morning boss'", "announce: market is open"] },

  // ── Charts extended ───────────────────────────────────────────────────────
  { id: "set_chart_count",   label: "Set chart count",       desc: "Open 1, 2, 3, or 4 chart panels", danger: "moderate",
    group: "Charts", icon: "🔢",
    examples: ["show 4 charts", "one chart only", "open 2 charts"] },

  // ── Trading extended ──────────────────────────────────────────────────────
  { id: "set_expiration",    label: "Set trade expiration",  desc: "Change expiry ticks/seconds/minutes for next trade", danger: "moderate",
    group: "Trading", icon: "⏱️",
    examples: ["use 5 ticks", "set expiry to 1 minute", "expire in 3 candles"] },
  { id: "set_trade_type",    label: "Set trade type",         desc: "Switch between Rise/Fall, Touch/No-Touch, Ends-Outside, etc.", danger: "moderate",
    group: "Trading", icon: "🎚️",
    examples: ["use rise/fall only", "switch to touches", "use ends-outside"] },

  // ── Account reads ─────────────────────────────────────────────────────────
  { id: "read_account_settings", label: "Read account settings", desc: "Show current stake, currency, default symbol, voice mode", danger: "read",
    group: "Account", icon: "⚙️",
    examples: ["my account settings", "what are my defaults"] },
  { id: "read_account_history", label: "Read account history", desc: "Show all-time P&L, total trades, days active", danger: "read",
    group: "Account", icon: "📊",
    examples: ["account history", "all-time P&L", "how long have I traded"] },

  // ── Analytics extended ────────────────────────────────────────────────────
  { id: "read_session_stats",  label: "Read session stats",  desc: "London, NY, Asian session performance breakdown", danger: "read",
    group: "Analytics", icon: "🌍",
    examples: ["session stats", "London vs NY P&L", "best session"] },
  { id: "read_strategy_timeline", label: "Read strategy timeline", desc: "Day-by-day strategy performance from analytics DB", danger: "read",
    group: "Analytics", icon: "📅",
    examples: ["strategy timeline", "how has R_25 done daily", "day-by-day P&L"] },

  // ── Backtests extended ────────────────────────────────────────────────────
  { id: "read_backtest_history", label: "Read backtest history", desc: "Show all backtests Optuna ran, ranked by Sharpe", danger: "read",
    group: "Backtests", icon: "🗂️",
    examples: ["recent backtests", "best backtest result", "what did Optuna find"] },
];

export const ACTION_BY_ID = Object.fromEntries(VOICE_ACTIONS.map(a => [a.id, a]));

export const DESTRUCTIVE_ACTIONS = new Set(
  VOICE_ACTIONS.filter(a => a.danger === "destructive").map(a => a.id)
);
export const MODERATE_ACTIONS = new Set(
  VOICE_ACTIONS.filter(a => a.danger === "moderate").map(a => a.id)
);

export const ACTIONS_BY_GROUP = VOICE_ACTIONS.reduce((acc, a) => {
  (acc[a.group] = acc[a.group] || []).push(a);
  return acc;
}, {});

// ─────────────────────────────────────────────────────────────────────────────
// BRIDGE — send voice-driven commands to AmberScanner (parent) when an action
// needs shared state (tab switch, trade execution, scanner subscription, ...).
// Falls back to local execution when the parent is reachable.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Dispatch a voice action to the parent (AmberScanner).
 * Returns true if the parent acknowledged, false if it wasn't reachable.
 */
export function dispatchToScanner(eventType, payload) {
  try {
    window.parent?.postMessage(
      { __voice_action__: true, type: eventType, payload, ts: Date.now() },
      window.location.origin
    );
    return true;
  } catch {
    return false;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// EXECUTE — runs an action using `ctx` (setTab, fetchBalance, etc.) + the bridge.
// ctx may be null in pure test contexts; in that case only no-ctx actions run.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {object} act       action object from Hermes reply (e.g. {type:"set_tab", tab:"charts"})
 * @param {object} ctx       reactive helpers from AgentTab.jsx / AmberScanner.jsx
 * @returns {Promise<{ok:boolean, message:string, data?:any}>}
 */
export const authHeaders = (ctx) => {
  // Try ctx.token first, then localStorage. Internal sub: kara-service also accepted.
  if (ctx && ctx.token) return { "Content-Type": "application/json", Authorization: `Bearer ${ctx.token}` };
  try {
    const tok = (typeof window !== "undefined" && window.localStorage && window.localStorage.getItem("kara_auth_token")) || "";
    return { "Content-Type": "application/json", Authorization: `Bearer ${tok}` };
  } catch { return { "Content-Type": "application/json" }; }
};

export async function executeAction(act, ctx = {}) {
  if (!act || !act.type) return { ok: false, message: "Empty action." };
  const meta = ACTION_BY_ID[act.type];
  if (!meta) {
    return { ok: false, message: `Unknown action: ${act.type}` };
  }

  // Two-Man Rule gate (2026-09-05 — blueprint §12). Destructive actions are
  // blocked unless the caller passes __confirmed = true AND a matching
  // approval token was issued within the last 30s. Caller (AmberScanner /
  // AgentTab) sees needsConfirm:true and must ask the user to confirm before
  // re-dispatching with act.__confirmed = true.
  const gate = confirmGateCheck(act);
  if (!gate.execute) {
    return {
      ok: false,
      message: gate.summary || gate.reason || "Action blocked pending confirmation.",
      needsConfirm: !!gate.needsConfirm,
      label: gate.label || describeAction(act),
      blockedReason: gate.reason || "needs_confirm",
    };
  }

  try {
    switch (act.type) {
      // ── Navigation — drive AmberScanner's tab state via bridge ─────────
      case "set_tab":
      case "show_strategy_tab":
      case "show_stratlab_tab":
      case "open_strategy_lab":
      case "open_charts":
      case "open_chart_dashboard":
      case "goto_charts": {
        const tab = act.tab
          || (act.type === "show_strategy_tab" ? "strategy" : null)
          || (act.type === "show_stratlab_tab" || act.type === "open_strategy_lab" ? "stratlab" : null)
          || (act.type === "open_charts" || act.type === "open_chart_dashboard" || act.type === "goto_charts" ? "charts" : null);
        const dispatched = dispatchToScanner("set_tab", { tab });
        if (!dispatched && ctx.setTab) ctx.setTab(tab);
        return { ok: true, message: `Switching to ${tab} tab.` };
      }
      case "open_chat":
      case "close_chat":
        dispatchToScanner("toggle_chat", { open: act.type === "open_chat" });
        return { ok: true, message: act.type === "open_chat" ? "Opening chat." : "Closing chat." };

      // ── Voice controls (local to AgentTab) ─────────────────────────────
      case "toggle_voice":
        if (ctx.toggleVoice) ctx.toggleVoice();
        return { ok: true, message: "Voice toggled." };
      case "toggle_wake_word":
        if (ctx.toggleWakeWord) ctx.toggleWakeWord();
        return { ok: true, message: "Wake word toggled." };
      case "set_personality":
        if (ctx.setPersonalityMode) ctx.setPersonalityMode(act.mode);
        return { ok: true, message: `Personality set to ${act.mode || "default"}.` };

      // ── Symbol management — bridge to AmberScanner ───────────────────
      case "add_symbol":
      case "remove_symbol":
      case "focus_symbol":
      case "add_all_symbols":
      case "clear_symbols":
        dispatchToScanner(act.type, { symbol: act.symbol });
        return { ok: true, message: `${act.type} dispatched.` };

      // ── Strategy — bridge (activate calls /api/strategies/activate) ────
      case "activate_strategy":
        if (act.name) dispatchToScanner("activate_strategy", { name: act.name });
        return { ok: true, message: `Activating ${act.name}.` };
      case "create_strategy":
        dispatchToScanner("create_strategy", { name: act.name, content: act.content, activate: act.activate });
        return { ok: true, message: `Creating ${act.name}.` };

      // ── Charts — bridge to AmberScanner chart state ───────────────────
      case "set_chart_symbol":
      case "set_chart_timeframe":
      case "set_chart_layout":
      case "set_chart_focus":
        dispatchToScanner(act.type, act);
        return { ok: true, message: `Chart updated.` };

      // ── Trading — bridge, but execute_trade is destructive ────────────
      case "execute_trade":
        dispatchToScanner("execute_trade", {
          symbol: act.symbol, direction: act.direction, stake: act.stake,
        });
        return { ok: true, message: `Executing ${act.direction || "CALL"} ${act.symbol || ""}.` };
      case "set_stake":
        dispatchToScanner("set_stake", { value: act.value });
        return { ok: true, message: `Stake set to $${act.value}.` };
      case "toggle_auto_trade":
        dispatchToScanner("toggle_auto_trade", { value: act.value });
        return { ok: true, message: "Auto-trade toggled." };

      // ── Alerts ────────────────────────────────────────────────────────
      case "toggle_alerts":
      case "toggle_alert_voice":
      case "clear_alerts":
        dispatchToScanner(act.type, { value: act.value });
        return { ok: true, message: `${act.type} dispatched.` };

      // ── History ───────────────────────────────────────────────────────
      case "clear_history":
        dispatchToScanner("clear_history");
        return { ok: true, message: "Trade history cleared." };

      // ── Telegram ──────────────────────────────────────────────────────
      case "send_telegram":
        dispatchToScanner("send_telegram", { message: act.message });
        return { ok: true, message: "Telegram queued." };

      // ── Backtests ─────────────────────────────────────────────────────
      case "run_backtest":
        dispatchToScanner("run_backtest", { symbol: act.symbol });
        return { ok: true, message: `Backtest started on ${act.symbol || "default"}.` };

      // ── AI scans ──────────────────────────────────────────────────────
      case "ai_scan":
      case "delegate_to_scout":
      case "delegate_to_strat":
      case "delegate_to_cortex":
      case "delegate_to_toolsmith":
        dispatchToScanner(act.type, act);
        return { ok: true, message: `${act.type} delegated.` };

      // ── READS — call APIs directly via fetch ──────────────────────────
      case "read_balance": {
        const r = await fetch("/api/balance", { method: "POST", headers: authHeaders(ctx) });
        const d = await r.json();
        const bal = d.balance ?? d?.accounts?.[0]?.balance ?? "unknown";
        return { ok: true, message: `Balance is $${Number(bal).toFixed(2)}.`, data: d };
      }
      case "read_account_mode": {
        const r = await fetch("/api/account/mode", { headers: authHeaders(ctx) });
        const d = await r.json();
        const mode = d.mode || d.account_mode || (d.is_virtual ? "demo" : "live");
        return { ok: true, message: `Account is on ${mode}.`, data: d };
      }
      case "read_active_strategy": {
        const r = await fetch("/api/strategies/active", { headers: authHeaders(ctx) });
        const d = await r.json();
        const name = d.active || d.strategy || d.name || "unknown";
        return { ok: true, message: `Active strategy: ${name}.`, data: d };
      }
      case "read_open_trades": {
        const r = await fetch("/api/active-trades", { headers: authHeaders(ctx) });
        const d = await r.json();
        const count = Array.isArray(d) ? d.length : (d.trades?.length ?? d.count ?? 0);
        return { ok: true, message: `${count} open trades.`, data: d };
      }
      case "read_history_summary": {
        const [h, s] = await Promise.all([
          fetch("/api/data/trades/summary", { headers: authHeaders(ctx) }).then(r => r.json()).catch(() => ({})),
          fetch("/api/analytics/live-wr", { headers: authHeaders(ctx) }).then(r => r.json()).catch(() => ({})),
        ]);
        const pnl = h.pnl ?? h.today_pnl ?? "unknown";
        const wr  = s.wr ?? s.win_rate ?? "unknown";
        const tot = h.total ?? h.today_trades ?? "unknown";
        return { ok: true, message: `Today: ${tot} trades, P&L $${pnl}, WR ${wr}%.`, data: { h, s } };
      }
      case "read_ml_status": {
        const r = await fetch("/api/ml/status", { headers: authHeaders(ctx) });
        const d = await r.json();
        const ok = d.online ?? d.ok ?? false;
        const acc = d.accuracy ?? d.last_accuracy ?? "unknown";
        return { ok: true, message: `ML is ${ok ? "online" : "offline"}; accuracy ${acc}.`, data: d };
      }
      case "read_list_strategies": {
        const r = await fetch("/api/strategies", { headers: authHeaders(ctx) });
        const d = await r.json();
        const names = Array.isArray(d) ? d.map(s => s.name || s).join(", ")
                     : (d.strategies || []).map(s => s.name || s).join(", ");
        return { ok: true, message: `Available: ${names || "none"}.`, data: d };
      }
      case "read_market_conditions": {
        const r = await fetch("/api/market-conditions/live", { headers: authHeaders(ctx) });
        const d = await r.json();
        return { ok: true, message: `Regime: ${d.regime || "unknown"}. Session: ${d.session || "—"}`, data: d };
      }
      case "read_best_hours": {
        const r = await fetch("/api/analytics/best-hours", { headers: authHeaders(ctx) });
        const d = await r.json();
        return { ok: true, message: `Best hours: ${(d.best_hours || d.hours || []).slice(0, 3).join(", ") || "computed"}.`, data: d };
      }
      case "read_signals": {
        const r = await fetch("/api/analytics/signals", { headers: authHeaders(ctx) });
        const d = await r.json();
        const arr = Array.isArray(d) ? d : (d.signals || []);
        return { ok: true, message: `${arr.length} recent signals.`, data: d };
      }

      // ── Speak ─────────────────────────────────────────────────────────
      case "speak":
        if (ctx.speak) ctx.speak(act.text || "");
        return { ok: true, message: "Spoken." };

      // ── Charts extended ──────────────────────────────────────────────
      case "set_chart_count": {
        const n = Math.max(1, Math.min(4, parseInt(act.count, 10) || 1));
        if (ctx.scannerApiRef && ctx.scannerApiRef.current && ctx.scannerApiRef.current.setChartLayout) {
          ctx.scannerApiRef.current.setChartLayout(n === 1 ? "single" : (n === 2 ? "dual" : (n === 3 ? "triple" : "quad")));
        }
        return { ok: true, message: `Chart count set to ${n}.` };
      }

      // ── Trading extended ─────────────────────────────────────────────
      case "set_expiration": {
        const v = act.value || act.expiration || act.expiry || "5";
        if (ctx.scannerApiRef && ctx.scannerApiRef.current && ctx.scannerApiRef.current.setStake) {
          // No dedicated setExpiry ref, but most scanners expose it via trade params;
          // we fall through to a speak so user gets feedback
        }
        return { ok: true, message: `Expiration set to ${v} (note: full wiring pending scanner setter).` };
      }
      case "set_trade_type": {
        const t = act.type || act.trade_type || act.contract_type || "rise_fall";
        return { ok: true, message: `Trade type set to ${t} (scanner setter pending).` };
      }

      // ── Account reads ────────────────────────────────────────────────
      case "read_account_settings": {
        return { ok: true, message: `User: ${ctx.userName || "anon"} · Currency: USD · Mode: ${ctx.accountMode || "unknown"} · Agent: ${ctx.agent || "kara"}`, data: { userName: ctx.userName, accountMode: ctx.accountMode, agent: ctx.agent } };
      }
      case "read_account_history": {
        const r = await fetch("/api/analytics/summary", { headers: authHeaders(ctx) }).then(x => x.json()).catch(() => ({}));
        return { ok: true, message: `Account history loaded (~${(r.trades||0)} trades).`, data: r };
      }

      // ── Analytics extended ────────────────────────────────────────────
      case "read_session_stats": {
        const r = await fetch("/api/analytics/session-stats", { headers: authHeaders(ctx) }).then(x => x.json()).catch(() => ({}));
        return { ok: true, message: `Session stats: ${JSON.stringify(r).slice(0, 200)}`, data: r };
      }
      case "read_strategy_timeline": {
        const r = await fetch("/api/analytics/strategy-timeline", { headers: authHeaders(ctx) }).then(x => x.json()).catch(() => ({}));
        return { ok: true, message: `Strategy timeline loaded.`, data: r };
      }

      // ── Backtests extended ────────────────────────────────────────────
      case "read_backtest_history": {
        const r = await fetch("/api/backtest/recent", { headers: authHeaders(ctx) }).then(x => x.json()).catch(() => ({}));
        return { ok: true, message: `Recent backtests loaded.`, data: r };
      }

      default:
        return { ok: false, message: `Action ${act.type} not wired in AgentTab executor yet.` };
    }
  } catch (err) {
    return { ok: false, message: `Action ${act.type} failed: ${err.message}` };
  }
}
